import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Card, BlockStack, Text, Box, Popover, Tooltip, Button, ButtonGroup, InlineStack,
} from '@shopify/polaris';
import { ChevronLeftIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import './CalendarPane.css';

// Rainbow palette. The SKU's BASE (highest) price picks a palette color; any
// lower prices for that same SKU become CONTRASTING colors from elsewhere in
// the palette (see DROP_OFFSETS / buildColorByPrice below) so a price drop
// reads as a clearly different hue rather than a shade of the same one.
export const PALETTE = [
  '#FB7B77', // coral
  '#FDC170', // peach
  '#F3F87F', // pale yellow
  '#98F786', // light green
  '#69EBFC', // cyan
  '#6D9EFC', // blue
  '#937DF8', // violet
  '#F78EF0', // pink
];
const GREY_NO_MAP = '#E0E0E0'; // grey for No Longer MAP
const EVENT_TEXT_COLOR = '#202223';

// Palette-index offsets applied (in order) to successive price drops below
// the anchor. The first drop jumps to the opposite side of the palette wheel
// for maximum contrast; later drops fill in the remaining slots so every
// distinct price for a SKU lands on a different palette color.
const DROP_OFFSETS = [4, 2, 6, 1, 5, 3, 7];

// Pick a palette slot for the SKU's anchor (highest) price. Same anchor price
// always lands on the same color so two SKUs that share a base price look
// related at a glance.
function anchorIndexForPrice(price) {
  const cents = Math.round(price * 100);
  const idx = (cents * 2654435761) >>> 0;
  return idx % PALETTE.length;
}

// Build a per-SKU price→color map. The highest distinct price ("anchor") gets
// a palette slot; each lower price is a palette-offset away from the anchor
// (see DROP_OFFSETS) so drops contrast with the base color instead of blending
// into a shade of it. `null` (No Longer MAP) is grey, unchanged.
export function buildColorByPrice(windows) {
  const distinctPrices = [
    ...new Set(windows.map((w) => w.price).filter((p) => p !== null)),
  ].sort((a, b) => b - a);
  const byPrice = new Map();
  if (distinctPrices.length === 0) return { byPrice, anchor: null };
  const anchorIdx = anchorIndexForPrice(distinctPrices[0]);
  const anchor = PALETTE[anchorIdx];
  distinctPrices.forEach((p, i) => {
    if (i === 0) {
      byPrice.set(p, anchor);
    } else {
      const offset = DROP_OFFSETS[(i - 1) % DROP_OFFSETS.length];
      byPrice.set(p, PALETTE[(anchorIdx + offset) % PALETTE.length]);
    }
  });
  return { byPrice, anchor };
}

function addDays(iso, days) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function diffDays(aIso, bIso) {
  const a = new Date(aIso + 'T00:00:00').getTime();
  const b = new Date(bIso + 'T00:00:00').getTime();
  return Math.round((b - a) / 86400000);
}

// Effective price for a given day across overlapping windows: lowest non-null
// price wins. If all candidates are null, the result is null (No Longer MAP).
function buildEffectiveSegments(windows) {
  if (windows.length === 0) return [];

  const sorted = [...windows].sort((a, b) => a.start_date.localeCompare(b.start_date));
  const minStart = sorted.reduce((m, w) => (w.start_date < m ? w.start_date : m), sorted[0].start_date);
  const maxEnd = sorted.reduce((m, w) => (w.end_date > m ? w.end_date : m), sorted[0].end_date);
  const totalDays = diffDays(minStart, maxEnd) + 1;

  const perDay = new Array(totalDays).fill(undefined);
  for (let i = 0; i < totalDays; i++) {
    const day = addDays(minStart, i);
    let best;
    for (const w of windows) {
      if (day < w.start_date || day > w.end_date) continue;
      if (best === undefined) {
        best = w;
        continue;
      }
      const bestPrice = best?.price ?? null;
      if (w.price === null && bestPrice !== null) continue;
      if (w.price !== null && bestPrice === null) { best = w; continue; }
      if (w.price !== null && bestPrice !== null && w.price < bestPrice) { best = w; }
    }
    perDay[i] = best;
  }

  const segments = [];
  let i = 0;
  while (i < totalDays) {
    const winner = perDay[i];
    if (winner === undefined) { i++; continue; }
    let j = i;
    while (j + 1 < totalDays && perDay[j + 1] === winner) j++;
    segments.push({
      start: addDays(minStart, i),
      end: addDays(minStart, j),
      window: winner ?? null,
    });
    i = j + 1;
  }
  return segments;
}

// Build the single-line bar label per the agreed format:
//   TIRO ES TR JK — JM1310 (TENABL/WHITE) * Back To School * $39.95
// For No Longer MAP (price=null):
//   TIRO ES TR JK — JM1310 (TENABL/WHITE) * No longer MAP-restricted
function formatBarLabel(ext) {
  const base = `${ext.description} — ${ext.article} (${ext.color})`;
  const w = ext.w;
  const price = w?.price ?? null;
  if (price === null) return `${base} * No longer MAP-restricted`;
  const windowName = w?.window_name ?? 'MAP';
  return `${base} * ${windowName} * $${price.toFixed(2)}`;
}

// Same content as formatBarLabel but rendered as spans with mixed weights:
// description (regular) — ARTICLE (bold) (color) (regular) * window * $price (bold).
// For off-MAP we render the off-MAP suffix in regular weight.
function BarLabel({ ext }) {
  const w = ext.w;
  const price = w?.price ?? null;
  const reg = { fontWeight: 400 };
  const bold = { fontWeight: 700 };
  if (price === null) {
    return (
      <>
        <span style={reg}>{ext.description}</span>
        <span style={reg}>{' — '}</span>
        <span style={bold}>{ext.article}</span>
        <span style={reg}>{' ('}</span>
        <span style={reg}>{ext.color}</span>
        <span style={reg}>{') * No longer MAP-restricted'}</span>
      </>
    );
  }
  const windowName = w?.window_name ?? 'MAP';
  return (
    <>
      <span style={reg}>{ext.description}</span>
      <span style={reg}>{' — '}</span>
      <span style={bold}>{ext.article}</span>
      <span style={reg}>{' ('}</span>
      <span style={reg}>{ext.color}</span>
      <span style={reg}>{`) * ${windowName} * `}</span>
      <span style={bold}>{`$${price.toFixed(2)}`}</span>
    </>
  );
}

export default function CalendarPane({ sku, onAnchorColorChange, seasonLabel }) {
  const [pop, setPop] = useState(null);
  const wrapRef = useRef(null);
  const calRef = useRef(null);
  const [view, setView] = useState('dayGridMonth');
  const [titleText, setTitleText] = useState('');

  // Per-SKU price→color map. Anchor (highest price) = palette; lower prices =
  // contrasting palette slots. Notify the parent so it can tint the
  // active card in the same hue.
  const { byPrice: colorByPrice, anchor: anchorColor } = useMemo(
    () => (sku ? buildColorByPrice(sku.windows) : { byPrice: new Map(), anchor: null }),
    [sku],
  );

  useEffect(() => {
    onAnchorColorChange?.(anchorColor);
  }, [anchorColor, onAnchorColorChange]);

  // Build events for the single active SKU.
  const events = useMemo(() => {
    if (!sku) return [];
    const out = [];
    const segments = buildEffectiveSegments(sku.windows);
    for (const seg of segments) {
      const w = seg.window;
      const price = w?.price ?? null;
      const bg = price === null ? GREY_NO_MAP : (colorByPrice.get(price) ?? GREY_NO_MAP);
      const ext = {
        w: w ?? null,
        description: sku.description,
        article: sku.article,
        color: sku.color,
        map_tier: sku.map_tier,
      };
      out.push({
        // title is used by the (default) plain text renderer in screen-reader
        // / list views. The real bar contents come from eventContent below.
        title: formatBarLabel(ext),
        start: seg.start,
        end: addDays(seg.end, 1), // FullCalendar end is exclusive
        backgroundColor: bg,
        borderColor: bg,
        textColor: EVENT_TEXT_COLOR,
        extendedProps: ext,
        display: 'block',
      });
    }
    return out;
  }, [sku, colorByPrice]);

  // Pick the earliest start of the active SKU as the initial calendar date so
  // the user lands on a month that has bars.
  const initialDate = useMemo(() => {
    if (!sku) return undefined;
    let earliest = null;
    for (const w of sku.windows) {
      if (!earliest || w.start_date < earliest) earliest = w.start_date;
    }
    return earliest ?? undefined;
  }, [sku]);

  // Header tier-summary string: each window appears once with its name; with
  // duplicates collapsed and "MAP" rendered as "Full Price Every Day". Joined
  // with " + " in chronological order of first appearance.
  const tierSummary = useMemo(() => {
    if (!sku || sku.windows.length === 0) return '';
    const seen = [];
    for (const w of [...sku.windows].sort((a, b) => a.start_date.localeCompare(b.start_date))) {
      const name = w.window_name === 'MAP' ? 'Full Price Every Day' : w.window_name;
      if (!seen.includes(name)) seen.push(name);
    }
    return seen.join(' + ');
  }, [sku]);

  // Keep the toolbar title in sync with the calendar's internal view title.
  useEffect(() => {
    const calApi = calRef.current?.getApi();
    if (calApi) setTitleText(calApi.view.title);
  }, [sku, view]);

  function switchView(v) {
    setView(v);
    const calApi = calRef.current?.getApi();
    if (calApi) {
      calApi.changeView(v);
      setTitleText(calApi.view.title);
    }
  }

  function goPrev() {
    const calApi = calRef.current?.getApi();
    if (calApi) { calApi.prev(); setTitleText(calApi.view.title); }
  }
  function goNext() {
    const calApi = calRef.current?.getApi();
    if (calApi) { calApi.next(); setTitleText(calApi.view.title); }
  }
  function goToday() {
    const calApi = calRef.current?.getApi();
    if (calApi) { calApi.today(); setTitleText(calApi.view.title); }
  }

  return (
    <div className="map-calendar-wrap">
      <Card>
        <BlockStack gap="300">
          {sku ? (
            <Box padding="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingLg" fontWeight="regular">
                  {sku.description}
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued">
                  {sku.article} · {sku.color} · {sku.business_segment}
                </Text>
                {tierSummary && (
                  <Text as="p" variant="bodyMd">{tierSummary}</Text>
                )}
                {sku.removal && sku.removal.how === 'dropped' ? (
                  <Text as="p" variant="bodyMd" tone="critical">
                    {`Dropped from Adidas's file dated ${sku.removal.removed_file_date || '?'} with no “Removed from MAP” flag. Last on MAP in the file dated ${sku.removal.last_seen_file_date || '?'} as “${sku.removal.last_tier}”. Confirm with Adidas before treating it as MAP-free.`}
                  </Text>
                ) : sku.removal ? (
                  <Text as="p" variant="bodyMd" tone="critical">
                    {`Removed from MAP by Adidas in the file dated ${sku.removal.removed_file_date || '?'} (last on MAP in the file dated ${sku.removal.last_seen_file_date || '?'} as “${sku.removal.last_tier}”): no MAP floor applies.`}
                  </Text>
                ) : sku.removed ? (
                  <Text as="p" variant="bodyMd" tone="critical">
                    Removed from MAP by Adidas in the current file: no MAP floor applies.
                  </Text>
                ) : sku.today.price === null && sku.today.window_name ? (
                  <Text as="p" variant="bodyMd" tone="critical">
                    Today: not MAP-restricted (the step-down ended and the MAP floor was removed)
                  </Text>
                ) : sku.today.price === null && sku.next_change ? (
                  <Text as="p" variant="bodyMd" tone="caution">
                    {`Today: no MAP window yet. MAP starts ${sku.next_change.date}${sku.next_change.price === null ? '' : ` at $${sku.next_change.price.toFixed(2)}`}.`}
                  </Text>
                ) : sku.today.price === null ? (
                  <Text as="p" variant="bodyMd" tone="caution">
                    Today: no MAP window on file after the last one ended. Adidas has not marked this SKU removed, so MAP may still apply: confirm with Adidas or upload the latest file.
                  </Text>
                ) : (
                  <Text
                    as="p"
                    variant="bodyMd"
                    tone={sku.today.window_name === 'MAP' ? 'success' : 'caution'}
                  >
                    {`Today: $${sku.today.price.toFixed(2)} (${sku.today.window_name ?? 'MAP'})`}
                  </Text>
                )}
                {sku.next_change && (
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {`Next: ${sku.next_change.date} → ${
                      sku.next_change.price === null
                        ? 'No longer MAP-restricted'
                        : `$${sku.next_change.price.toFixed(2)} (${sku.next_change.window_name})`
                    }`}
                  </Text>
                )}
              </BlockStack>
            </Box>
          ) : (
            <Text as="h2" variant="headingMd">{seasonLabel || 'MAP'}</Text>
          )}

          {!sku ? (
            <Box padding="1600">
              <BlockStack gap="200" inlineAlign="center">
                <Text as="p" variant="headingSm" tone="subdued">
                  Search a SKU on the left to see its MAP schedule.
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Try a SKU like <code>EF8472</code>, a UPC, or any part of a product description.
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Only the windows in Adidas&apos;s spreadsheet count: a SKU with no holiday window stays at full price through the holidays. Dates are inclusive on both ends. When windows overlap, the lowest price applies.
                </Text>
              </BlockStack>
            </Box>
          ) : (
            <div ref={wrapRef} style={view === 'multiMonthYear' ? undefined : { minHeight: 765 }}>
              <Box paddingBlockEnd="300">
                <InlineStack align="space-between" blockAlign="center" wrap={false}>
                  <ButtonGroup>
                    <Button icon={ChevronLeftIcon} onClick={goPrev} accessibilityLabel="Previous month" />
                    <Button icon={ChevronRightIcon} onClick={goNext} accessibilityLabel="Next month" />
                    <Button onClick={goToday}>Today</Button>
                  </ButtonGroup>
                  <Text as="h2" variant="headingLg">{titleText}</Text>
                  <ButtonGroup variant="segmented">
                    <Button
                      pressed={view === 'dayGridMonth'}
                      onClick={() => switchView('dayGridMonth')}
                    >
                      Month
                    </Button>
                    <Button
                      pressed={view === 'multiMonthYear'}
                      onClick={() => switchView('multiMonthYear')}
                    >
                      Year
                    </Button>
                  </ButtonGroup>
                </InlineStack>
              </Box>
              <FullCalendar
                ref={calRef}
                plugins={[dayGridPlugin, multiMonthPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={false}
                views={{
                  multiMonthYear: {
                    type: 'multiMonth',
                    duration: { months: 12 },
                    multiMonthMaxColumns: 2,
                    multiMonthMinWidth: 350,
                  },
                }}
                initialDate={initialDate}
                events={events}
                datesSet={(arg) => setTitleText(arg.view.title)}
                eventContent={(arg) => {
                  const ext = arg.event.extendedProps;
                  const label = formatBarLabel(ext);
                  return (
                    <Tooltip content={label} preferredPosition="above" dismissOnMouseOut>
                      <div className="map-bar"><BarLabel ext={ext} /></div>
                    </Tooltip>
                  );
                }}
                eventClick={(info) => {
                  const ext = info.event.extendedProps;
                  const rect = info.el.getBoundingClientRect();
                  setPop({ x: rect.left, y: rect.bottom, ext });
                }}
                height={view === 'multiMonthYear' ? 'auto' : 765}
              />
            </div>
          )}

          {pop && pop.ext.w && (
            <Popover
              active
              activator={<div style={{ position: 'fixed', left: pop.x, top: pop.y }} />}
              onClose={() => setPop(null)}
            >
              <Box padding="300">
                <BlockStack gap="100">
                  <Text as="p" variant="headingSm">{pop.ext.description}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {pop.ext.article} · {pop.ext.color} · {pop.ext.map_tier}
                  </Text>
                  <Text as="p">{pop.ext.w.window_name}</Text>
                  <Text as="p">{pop.ext.w.start_date} → {pop.ext.w.end_date}</Text>
                  <Text as="p">{pop.ext.w.price === null ? 'No price (off-MAP)' : `$${pop.ext.w.price.toFixed(2)}`}</Text>
                </BlockStack>
              </Box>
            </Popover>
          )}
        </BlockStack>
      </Card>
    </div>
  );
}
